import {
  Wallet,
  TrendingUp,
  ArrowDownCircle,
  ArrowUpCircle,
} from 'lucide-react';
import { CashSummaryDTO } from '../../types/cash/cashSummary';

interface Props {
  data?: CashSummaryDTO | null;
  showNoSession?: boolean;
}

const CashSummaryView: React.FC<Props> = ({ data, showNoSession }) => {
  /**
   * 🧠 Fallback seguro
   */
  const safe = {
    opening: data?.openingAmount ?? 0,
    sales: data?.salesAmount ?? 0,
    income: data?.incomeAmount ?? 0,
    withdrawal: data?.withdrawalAmount ?? 0,
    refund: data?.refundAmount ?? 0,
    adjustmentIn: data?.adjustmentInAmount ?? 0,
    adjustmentOut: data?.adjustmentOutAmount ?? 0,
    receivablePayments: data?.receivablePaymentAmount ?? 0,
    payablePayments: data?.payablePaymentAmount ?? 0,
    expected: data?.expectedAmount ?? 0,
  };

  const totalColor =
    safe.expected < 0
      ? 'text-danger'
      : safe.expected === 0
      ? 'text-gray-400'
      : 'text-success';

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      {/* HEADER */}
      <div className="border-b border-stroke px-6 py-4 dark:border-strokedark">
        <h3 className="flex items-center justify-between font-medium text-black dark:text-white">
          <span className="flex items-center gap-2">
            <Wallet size={18} />
            Resumen de Efectivo
          </span>

          {showNoSession && (
            <span className="text-xs font-medium text-warning">
              Sin sesión activa
            </span>
          )}
        </h3>
      </div>

      {/* BODY */}
      <div className="space-y-4 p-6 text-sm">
        {/* 🔹 MONTO INICIAL */}
        <Item
          icon={<ArrowUpCircle size={16} />}
          label="Monto inicial"
          value={safe.opening}
        />

        {/* 🔹 ENTRADAS */}
        <div className="border-t border-stroke pt-2">
          <p className="text-gray-400 mb-2 text-xs">Entradas</p>

          <Item
            icon={<TrendingUp size={16} />}
            label="Ventas efectivo"
            value={safe.sales}
            success
          />
          <Item
            icon={<ArrowUpCircle size={16} />}
            label="Ingresos"
            value={safe.income}
            success
          />
          <Item
            icon={<ArrowUpCircle size={16} />}
            label="Ajustes"
            value={safe.adjustmentIn}
            success
          />
          <Item
            icon={<ArrowUpCircle size={16} />}
            label="Cobro a Clientes"
            value={safe.receivablePayments}
            success
          />
        </div>

        {/* 🔹 SALIDAS */}
        <div className="border-t border-stroke pt-2">
          <p className="text-gray-400 mb-2 text-xs">Salidas</p>

          <Item
            icon={<ArrowDownCircle size={16} />}
            label="Retiros"
            value={safe.withdrawal}
            danger
          />
          <Item
            icon={<ArrowDownCircle size={16} />}
            label="Reembolsos"
            value={safe.refund}
            danger
          />
          <Item
            icon={<ArrowDownCircle size={16} />}
            label="Ajustes"
            value={safe.adjustmentOut}
            danger
          />
          <Item
            icon={<ArrowDownCircle size={16} />}
            label="Pagos a Proveedores"
            value={safe.payablePayments}
            danger
          />
        </div>

        {/* 🔹 TOTAL */}
        <div className="flex items-center justify-between border-t border-stroke pt-4 text-base font-semibold">
          <span>Efectivo esperado</span>
          <span className={`text-lg ${totalColor}`}>
            ${safe.expected.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CashSummaryView;


const Item = ({
  icon,
  label,
  value,
  success,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  success?: boolean;
  danger?: boolean;
}) => {

  const color = danger
    ? 'text-danger dark:text-red-400'
    : success
    ? 'text-success dark:text-green-400'
    : 'text-black dark:text-white';

  return (
    <div className="flex items-center justify-between">
      
      {/* LABEL */}
      <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
        
        {/* 🔥 ICONO CON COLOR */}
        <span className={color}>
          {icon}
        </span>

        {label}
      </span>

      {/* VALUE */}
      <span className={`font-medium ${color}`}>
        ${value.toFixed(2)}
      </span>

    </div>
  );
};